// src/mixins/useInsuranceFormMixin.js
import useValidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";

export default {
    data() {
        return {
            page: 'home',
            formData: {
                travel_reason: null,
                regional_coverage: null,
                traveling_by: null,
                storno_type: 'ADDON',
                insurance_from: null,
                insurance_to: null,
                age_above_70: false,
                trip_cancellation_coverage: null,
                trip_cancellation_reference_date: new Date().toLocaleDateString('sv-SE'),
                additional_insurances: [],
                travellers_count: 1,
            },
            requiredFields: {
                travel_reason: true,
                regional_coverage: true,
                traveling_by: true,
                insurance_from: null,
                insurance_to: null,
            },
            priceAfterAdditionalInsurances: 0,
            travellers_count_input: 1,
        };
    },
    setup() {
        return {
            v$: useValidate({ $autoDirty: true }),
        };
    },
    validations() {
        return {
            travellers_count_input: {
                required,
                range(value) {
                    if (!value) return true;
                    return !(value < 1 || value > 4);
                }
            },
            formData: {
                insurance_from: {
                    required,
                    dateBeforeOrEqual(value) {
                        if (!value || !this.formData.insurance_to) return true;
                        return new Date(value).setHours(0, 0, 0, 0) <= new Date(this.formData.insurance_to).setHours(0, 0, 0, 0);
                    },
                    notBeforeToday(value) {
                        if (!value) return true;
                        return new Date(value).setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0);
                    }
                },
                insurance_to: {
                    required,
                    dateRange(value) {
                        if (!value || !this.formData.insurance_from) return true;
                        return this.daysCount <= 90;
                    },
                    euDay(value) {
                        if (!value || !this.formData.insurance_from || this.product !== 'short_eu_day') return true;
                        return this.dateDiff(this.formData.insurance_from, this.formData.insurance_to) === 1;
                    }
                },
                trip_cancellation_reference_date: {
                    rules30(value) {
                        if (!value) return true;
                        return this.dateDiff(new Date(this.formData.insurance_from), new Date()) > 30 ||
                            this.dateDiff(this.formData.trip_cancellation_reference_date, new Date()) === 0;
                    },
                    required(value) {
                        return value || this.formData.storno_type === "NONE";
                    }
                },
                trip_cancellation_coverage: {
                    required(value) {
                        return value || this.formData.storno_type === "NONE";
                    },
                    limit(value) {
                        return value <= this.tripCancellationPriceLimit;
                    }
                }
            }
        };
    },
    computed: {
        product() {
            if (!this.formData.travel_reason || !this.formData.regional_coverage || !this.formData.insurance_from) return null;
            if (this.formData.storno_type === 'SEPARATE') return 'storno';
            if (this.formData.regional_coverage === 'SLOVAKIA' && this.arrayIncludesArray(['FESTIVAL', 'CONCERT'], this.formData.travel_reason)) return 'short_sk_event';
            if (this.formData.regional_coverage === 'EUROPE' && this.arrayIncludesArray(['CONCERT'], this.formData.travel_reason)) return 'short_eu_day';
            return 'short_eu_world';
        },
        minDateTo() {
            return !this.formData.insurance_from ? new Date() : new Date(this.formData.insurance_from);
        },
        tripCancellationPrice() {
            return this.formData.trip_cancellation_coverage * (this.product === 'storno' ? 0.0511 : 0.04);
        },
        daysCount() {
            return this.dateDiff(this.formData.insurance_from, this.formData.insurance_to) + (this.dateDiffMode === 'HOURS' ? 0 : 1);
        },
        dateDiffMode() {
            return this.product === 'short_eu_day' ? 'HOURS' : 'DAYS';
        },
        basePrice() {
            if (!this.formData.insurance_from || !this.formData.insurance_to || this.formData.storno_type === 'SEPARATE') return 0;
            const prices = {
                normal: {
                    short_eu_day: 1.95,
                    short_eu_world: 1.95,
                    short_sk_event: 0.7,
                },
                higher: {
                    short_eu_world: 2.80,
                }
            };
            const product = this.product;
            const ageMultiplier = this.formData.age_above_70 ? 2 : 1;
            const pricePerUnit = prices[this.formData.regional_coverage === 'WORLD' ? 'higher' : 'normal'][product] * ageMultiplier;
            return pricePerUnit * this.daysCount * this.formData.travellers_count;
        },
        totalPrice() {
            return this.priceAfterAdditionalInsurances + this.tripCancellationPrice;
        },
        computedFormData() {
            return JSON.stringify(this.formData);
        },
        tripCancellationPriceLimit() {
            return this.formData.travellers_count * 10000;
        }
    },
    watch: {
        computedFormData(newJsonValue, oldJsonValue) {
            const newValue = JSON.parse(newJsonValue);
            const oldValue = JSON.parse(oldJsonValue);
            for (const key in newValue) {
                if (JSON.stringify(newValue[key]) !== JSON.stringify(oldValue[key])) {
                    if (Object.prototype.hasOwnProperty.call(this.requiredFields, key)) {
                        this.resetNextFields(key);
                    }
                    break;
                }
            }
        },
        product(newProduct, oldProduct) {
            if (this.formData.insurance_from && newProduct === 'short_eu_day') {
                this.updateDateToNextDay();
            }
            if (newProduct && !oldProduct && this.formData.storno_type !== 'NONE') {
                this.formData.trip_cancellation_reference_date = this.todayAsDateString();
            }
        },
        'formData.insurance_from'(newVal) {
            if (newVal && this.product === 'short_eu_day') {
                this.updateDateToNextDay();
            }
        },
        'formData.storno_type'(newVal) {
            if (newVal === 'NONE') {
                this.formData.trip_cancellation_reference_date = null;
                this.formData.trip_cancellation_coverage = null;
                this.v$.formData.trip_cancellation_reference_date.$reset();
                this.v$.formData.trip_cancellation_coverage.$reset();
            } else if (newVal === 'ADDON' || newVal === 'SEPARATE') {
                this.formData.trip_cancellation_reference_date = this.todayAsDateString();
            }
            if (newVal === null) {
                this.formData.storno_type = 'ADDON';
            }
        },
        travellers_count_input(val) {
            this.formData.travellers_count = Math.min(4, Math.max(1, val));
        }
    },
    methods: {
        updateDateToNextDay() {
            const date = new Date(this.formData.insurance_from);
            date.setDate(date.getDate() + 1);
            this.formData.insurance_to = date.toISOString().split('T')[0];
        },
        todayAsDateString() {
            return new Date().toLocaleDateString('sv-SE');
        },
        arrayIncludesArray(arr, target) {
            return target.every(item => arr.includes(item));
        },
        updatePriceAfterAdditionals(value) {
            this.priceAfterAdditionalInsurances = value;
        },
        insuranceStartMin() {
            const date = new Date();
            return date;
        },
        dateDiff(date1, date2) {
            if (!date1 || !date2) return 0;
            const toDayStart = (d) => {
                const s = typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)
                    ? d.split('T')[0]
                    : null;
                if (s) {
                    const [y, m, day] = s.split('-').map(Number);
                    return Date.UTC(y, m - 1, day);
                }
                const x = new Date(d);
                return Date.UTC(x.getFullYear(), x.getMonth(), x.getDate());
            };
            const t1 = toDayStart(date1);
            const t2 = toDayStart(date2);
            return Math.abs(Math.floor((t2 - t1) / (1000 * 60 * 60 * 24)));
        },
        goToForm() {
            this.v$.formData.$touch();
            if (!this.v$.formData.$invalid) {
                this.page = 'form';
            } else {
                event.preventDefault();
                return false;
            }
        },
        goToHome() {
            this.page = 'home';
        },
        formatPrice(price) {
            return new Intl.NumberFormat("sk-SK", {
                style: "currency",
                currency: "EUR",
                maximumFractionDigits: 2
            }).format(price);
        },
        resetNextFields(fieldName) {
            let shouldClear = false;
            for (const key in this.requiredFields) {
                if (shouldClear) {
                    // Keep existing insurance_to when insurance_from changes;
                    // validation will handle any invalid combinations.
                    if (!(fieldName === 'insurance_from' && key === 'insurance_to')) {
                        this.formData[key] = null;
                    }
                }
                if (key === fieldName) shouldClear = true;
            }
        }
    }
}
